import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { notify } from '../../utils/notifications';
import { X, Euro } from 'lucide-react';

export const METODI_PAGAMENTO = [
  { value: 'CONTANTI', label: 'Contanti' },
  { value: 'BONIFICO', label: 'Bonifico' },
  { value: 'CARTA', label: 'Carta' },
  { value: 'PAYPAL', label: 'PayPal' },
  { value: 'ALTRO', label: 'Altro' },
];

const oggiISO = () => new Date().toISOString().split('T')[0];

/**
 * Conferma dell'incasso di una singola mensilità.
 *
 * Chiede sempre metodo e data: registrare un incasso è un'operazione
 * contabile, non deve partire da un clic isolato.
 */
const ModalIncassoRata = ({ rata, onClose, onConfermato }) => {
  const [metodo, setMetodo] = useState('CONTANTI');
  const [dataPagamento, setDataPagamento] = useState(oggiISO());
  const [note, setNote] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);

  useEffect(() => {
    if (rata) {
      setMetodo('CONTANTI');
      setDataPagamento(oggiISO());
      setNote('');
    }
  }, [rata]);

  if (!rata) return null;

  const conferma = async (e) => {
    e.preventDefault();
    setSalvataggio(true);
    const toastId = notify.loading('Registrazione incasso...');
    try {
      await api.post(`/abbonamenti/rate/${rata.id}/segna_pagata/`, {
        metodo,
        data_pagamento: dataPagamento,
        note,
      });
      notify.update(toastId, {
        type: 'success',
        render: `Incassata ${rata.etichetta_periodo} — €${Number(rata.importo).toFixed(2)}`,
      });
      onClose();
      onConfermato?.();
    } catch (error) {
      notify.update(toastId, {
        type: 'error',
        render: error.response?.data?.errore || 'Errore nella registrazione',
      });
    } finally {
      setSalvataggio(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
        <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-900">Registra incasso</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={conferma} className="p-5 space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Mensilità</p>
              <p className="text-sm font-bold text-gray-900 capitalize">{rata.etichetta_periodo}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Importo</p>
              <p className="text-xl font-bold text-gray-900 flex items-center gap-1">
                <Euro className="w-4 h-4 text-gray-400" />
                {Number(rata.importo).toFixed(2)}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Metodo di pagamento *
            </label>
            <div className="grid grid-cols-3 gap-2">
              {METODI_PAGAMENTO.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMetodo(m.value)}
                  className={`px-3 py-2 text-sm rounded-lg border transition font-medium ${
                    metodo === m.value
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-gray-900'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Data incasso *</label>
            <input
              type="date"
              required
              value={dataPagamento}
              onChange={(e) => setDataPagamento(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Note</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Facoltative"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
            />
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold text-sm"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={salvataggio}
              className="px-5 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition font-semibold text-sm disabled:opacity-40"
            >
              {salvataggio ? 'Registrazione...' : 'Conferma incasso'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalIncassoRata;
